import {
  faEye,
  faEyeSlash,
  faLock,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../css/JuanIS/JuanISLogin.css";
import JuanEMSLogo from "../../images/JuanEMSlogo.png";
import SJDEFILogo from "../../images/SJDEFILogo.png";
import PasswordNotification from "../JuanScope/PasswordNotification";
import { Turnstile } from '@marsidev/react-turnstile';

function JuanISLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userType: "",
      employeeID: "",
      password: "",
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isTurnstileReady, setIsTurnstileReady] = useState(true);

  useEffect(() => {
    if (location.state?.fromPasswordReset) {
      toast.success('Password reset successful. Please check your email for the verification code.', {
        position: "top-center",
      });
    }
  }, [location.state]);

  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const validateForm = (formData) => {
    if (!captchaToken) {
      showErrorToast("Please complete the CAPTCHA verification");
      return false;
    }
    return true;
  };

  const handleTurnstileError = () => {
    showErrorToast("An error occurred while verifying CAPTCHA. Please try again.");
  };

  const onSubmit = async (formData) => {
    if (!validateForm(formData)) {
      return;
    }

    setLoading(true);

    if (formData.userType === "Student") {
      // Placeholder for student login: navigate to student dashboard without validation
      navigate("/juan-is-student-dashboard");
      toast.info("Student login is currently a placeholder. Redirecting to student dashboard.", {
        position: "top-center",
      });
      setLoading(false);
      return;
    }

    // Faculty login logic (unchanged)
    if (formData.userType === "Faculty") {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/faculty/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            employeeID: formData.employeeID.trim(),
            password: formData.password.trim(),
            captchaToken,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (data.errorType === 'account_inactive') {
            showErrorToast("Account is inactive. Please contact the admin for support.");
          } else if (data.errorType === 'login_lockout') {
            showErrorToast(`Account is locked. Try again in ${Math.ceil(data.timeLeft / 60)} minutes.`);
          } else if (data.errorType === 'validation') {
            showErrorToast(data.message || "Invalid Employee No or password format.");
          } else if (data.errorType === 'authentication') {
            showErrorToast("Invalid credentials. Please try again.");
          } else if (data.errorType === 'email_failure') {
            showErrorToast("Failed to send verification code. Please try again.");
          } else if (data.errorType === 'captcha_invalid') {
            showErrorToast("Invalid CAPTCHA. Please try again.");
          } else {
            showErrorToast(data.message || "Login failed. Please try again.");
          }
          return;
        }

        navigate("/juanis-verify-otp", {
          state: {
            email: data.email,
            firstName: data.firstName,
            employeeID: formData.employeeID,
            isLoginOtp: true,
            status: data.status,
            otpExpires: data.otpExpires, // Include otpExpires for OTP verification
          },
        });
        toast.success("Verification code sent to your email.", {
          position: "top-center",
        });
      } catch (error) {
        console.error("Login error:", error);
        showErrorToast("Failed to connect to the server. Please check your network or try again later.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleGoToHome = () => {
    navigate("/home");
  };

  return (
    <div className="juanis-login-container">
      <ToastContainer />
      <PasswordNotification />
      <div className="juanis-login-left-side">
        <div className="juanis-login-image-background">
          <div className="juanis-login-image-overlay"></div>
          <div className="juanis-login-left-content">
            <div className="juanis-login-top-logo">
              <img
                src={SJDEFILogo}
                alt="SJDEFI Logo"
                className="juanis-login-sjdefi-logo"
              />
              <div className="juanis-login-top-text">
                <h1>SAN JUAN DE DIOS EDUCATIONAL FOUNDATION, INC.</h1>
                <p className="juanis-login-motto">
                  Where faith and reason are expressed in Charity.
                </p>
              </div>
            </div>
            <div className="juanis-login-center-logo">
              <img
                src={JuanEMSLogo}
                alt="JuanEMS Logo"
                className="juanis-login-ems-logo"
              />
              <h2 className="juanis-login-ems-title">JuanEMS</h2>
              <p className="juanis-login-ems-subtitle">
                Juan Enrollment Management System
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="juanis-login-right-side">
        <div className="juanis-login-form-container">
          <div className="juanis-login-scope-title">
            <h1>JUAN IS</h1>
            <p className="juanis-login-scope-subtitle">
              Online Information System
            </p>
          </div>
          <div className="juanis-login-login-form">
            <h2 className="juanis-login-form-title">Login</h2>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="juanis-login-form-group">
                <div className="juanis-login-input-label">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="juanis-login-input-icon"
                  />
                  <label>Select User Type</label>
                </div>
                <select
                  className="juanis-login-input-field"
                  {...register("userType", { required: "User type is required" })}
                >
                  <option value="" disabled>
                    Select user type
                  </option>
                  <option value="Student">Student</option>
                  <option value="Faculty">Faculty</option>
                </select>
                {errors.userType && (
                  <p className="juanis-error-message">{errors.userType.message}</p>
                )}
              </div>

              <div className="juanis-login-form-group">
                <div className="juanis-login-input-label">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="juanis-login-input-icon"
                  />
                  <label>Student No./Employee No.</label>
                </div>
                <input
                  type="text"
                  placeholder="Enter your Student No. or Employee No. (e.g., 25-00008)"
                  className="juanis-login-input-field"
                  {...register("employeeID", {
                    required: "Student No./Employee No. is required",
                    pattern: formData => {
                      if (formData.userType === "Faculty") {
                        return {
                          value: /^\d{2}-\d{5}$/,
                          message: "Invalid Employee No. format (e.g., 25-00008)",
                        };
                      }
                      return true; // No pattern validation for Student
                    },
                  })}
                />
                {errors.employeeID && (
                  <p className="juanis-error-message">{errors.employeeID.message}</p>
                )}
              </div>

              <div className="juanis-login-form-group">
                <div className="juanis-login-input-label">
                  <FontAwesomeIcon
                    icon={faLock}
                    className="juanis-login-input-icon"
                  />
                  <label>Password</label>
                </div>
                <div className="juanis-login-password-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="juanis-login-input-field"
                    {...register("password", {
                      required: "Password is required",
                      minLength: formData => {
                        if (formData.userType === "Faculty") {
                          return {
                            value: 8,
                            message: "Password must be at least 8 characters",
                          };
                        }
                        return true; // No minLength validation for Student
                      },
                    })}
                  />
                  <button
                    type="button"
                    className="juanis-login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {errors.password && (
                  <p className="juanis-error-message">{errors.password.message}</p>
                )}
              </div>

              <div className="juanis-login-turnstile-container" style={{ marginTop: '15px', marginBottom: '15px' }}>
                {isTurnstileReady && (
                  <Turnstile
                    siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => {
                      setCaptchaToken(token);
                    }}
                    onError={handleTurnstileError}
                    onExpire={() => {
                      setCaptchaToken(null);
                      handleTurnstileError();
                    }}
                    options={{
                      theme: 'light',
                      size: 'normal',
                      retry: 'auto',
                      retryInterval: 3000,
                    }}
                    scriptOptions={{
                      async: true,
                      defer: true,
                      appendTo: 'head',
                    }}
                  />
                )}
              </div>

              <div className="juanis-login-links-container">
                <button
                  type="button"
                  className="juanis-login-go-home-btn"
                  onClick={handleGoToHome}
                >
                  Go back to Home
                </button>
                <button
                  type="button"
                  className="juanis-login-forgot-password-btn"
                  onClick={() => navigate("/juanis-forgot-password")}
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                className="juanis-login-login-button"
                disabled={loading}
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JuanISLogin;